import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIns, type CheckIn } from '@/lib/api/v1';
import { Badge, Card, EmptyState, Icon, Skeleton } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import { useSession, useToasts } from '@/lib/store';

/**
 * Activity feed — household-scoped timeline of logged tastings.
 *
 * The visibility model means private tasting logs are author-only; household
 * is the default ("everyone in your kjeller can see"); friends will become
 * cross-tenant once the friend graph lands; public is shareable.
 *
 * For now the household tab covers the common case. The "mine" filter
 * shows the author their own activity (any visibility).
 */

type FeedTab = 'household' | 'mine';

const SCOPE_TABS: { id: FeedTab; label: string; icon: 'users' | 'user' }[] = [
  { id: 'household', label: 'Husholdning', icon: 'users' },
  { id: 'mine', label: 'Mine', icon: 'user' },
];

export function FeedPage() {
  const [tab, setTab] = useState<FeedTab>('household');

  const feedQ = useQuery({
    queryKey: ['feed', tab],
    queryFn: () => CheckIns.list({ scope: tab, limit: 50 }),
    refetchInterval: 30_000,
  });

  const items = feedQ.data ?? [];

  return (
    <div className="grid gap-6 max-w-3xl">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Aktivitet</h1>
        <p className="text-sm text-mist">
          Hva drikkes i kjelleren — smakinger, vurderinger og notater i sanntid.
        </p>
      </header>

      <div className="flex gap-2">
        {SCOPE_TABS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setTab(s.id)}
            className={`ui-pill ${tab === s.id ? 'ui-pill--active' : ''}`}
          >
            <Icon name={s.icon} size={12} />
            {s.label}
          </button>
        ))}
      </div>

      {feedQ.isLoading ? (
        <div className="grid gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="grid gap-3 p-4">
              <Skeleton height={20} width="40%" />
              <Skeleton height={80} />
              <Skeleton height={14} width="60%" />
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="check-circle"
          title="Ingen smakinger enda"
          description={
            tab === 'mine'
              ? 'Du har ikke logget noen smakinger enda. Trykk "Logg smaking" på en flaske for å starte.'
              : 'Ingen i husholdningen har logget smakinger enda.'
          }
        />
      ) : (
        <ul className="grid gap-3">
          {items.map((c) => (
            <FeedItem key={c.id} checkIn={c} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FeedItem({ checkIn }: { checkIn: CheckIn }) {
  const linkTarget = checkIn.bottleId
    ? `/bottle/${checkIn.bottleId}`
    : checkIn.productId
      ? `/bottle/${checkIn.productId}`
      : undefined;

  const meta = [checkIn.snapshotRegion, checkIn.snapshotCountry, checkIn.snapshotVintage]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card className="grid gap-3 p-4">
      <div className="flex items-start gap-3">
        {/* Author avatar — first letter of userId until profile pages exist */}
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-candle/10 text-candle text-xs uppercase">
          {checkIn.userId.charAt(0)}
        </div>
        <div className="grid flex-1 gap-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm text-parchment font-medium truncate">
              {checkIn.userId === 'anonymous' ? 'Ukjent' : checkIn.userId}
            </span>
            <span className="text-xs text-mist">logget smaking</span>
            {checkIn.servingStyle && (
              <Badge tone="mist">{servingLabel(checkIn.servingStyle)}</Badge>
            )}
            <span className="ml-auto text-[10px] uppercase tracking-widest text-mist">
              {formatRelative(checkIn.createdAt)}
            </span>
          </div>

          {/* Body card with bottle + rating + comment */}
          <div className="grid grid-cols-[auto_1fr] gap-3 mt-1">
            <div className="relative grid h-20 w-14 place-items-center overflow-hidden rounded bg-ink-soft sm:h-24 sm:w-16">
              <BottleImage
                src={checkIn.snapshotImageUrl}
                alt={checkIn.snapshotName}
                iconSize={20}
                mode="lightbox"
              />
            </div>
            <div className="grid gap-1 min-w-0">
              {linkTarget ? (
                <Link
                  to={linkTarget}
                  className="font-serif text-base leading-tight text-parchment hover:text-candle line-clamp-2"
                >
                  {checkIn.snapshotName}
                </Link>
              ) : (
                <span className="font-serif text-base text-parchment line-clamp-2">{checkIn.snapshotName}</span>
              )}
              {meta && <span className="text-xs text-mist">{meta}</span>}
              {checkIn.rating != null && checkIn.rating > 0 && <StarRating value={checkIn.rating} />}
              {checkIn.comment && (
                <p className="mt-1 text-sm text-parchment-dim line-clamp-3">"{checkIn.comment}"</p>
              )}
              {(checkIn.withFood || checkIn.venueName) && (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-mist">
                  {checkIn.withFood && (
                    <span className="flex items-center gap-1">
                      <Icon name="utensils" size={10} /> {checkIn.withFood}
                    </span>
                  )}
                  {checkIn.venueName && (
                    <span className="flex items-center gap-1">
                      <Icon name="map-pin" size={10} /> {checkIn.venueName}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Reactions footer — toast (like) other people's tastings. */}
          <div className="mt-2 flex items-center gap-3 border-t border-line-soft pt-2">
            <ToastButton checkIn={checkIn} />
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Toast/like button. Optimistically flips the local cache so the heart
 * fills before the network round-trip. Authors can't toast their own
 * tasting logs (server enforces) — we surface that as a disabled, dimmed
 * button rather than hiding it, so users can see *their* toast count.
 */
function ToastButton({ checkIn }: { checkIn: CheckIn }) {
  const qc = useQueryClient();
  const { user } = useSession();
  const { push } = useToasts();
  const isOwn = user?.id === checkIn.userId;
  const toasted = Boolean(checkIn.viewerHasToasted);

  const mutate = useMutation({
    mutationFn: async (): Promise<{ toastCount: number }> =>
      toasted ? CheckIns.untoast(checkIn.id) : CheckIns.toast(checkIn.id),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['feed'] });
      const snapshots = qc.getQueriesData<CheckIn[]>({ queryKey: ['feed'] });
      // Optimistically flip every cached feed list that contains this check-in.
      for (const [key, data] of snapshots) {
        if (!Array.isArray(data)) continue;
        qc.setQueryData<CheckIn[]>(key, (old) =>
          old?.map((c) =>
            c.id === checkIn.id
              ? {
                  ...c,
                  viewerHasToasted: !toasted,
                  toastCount: Math.max(0, c.toastCount + (toasted ? -1 : 1)),
                }
              : c,
          ),
        );
      }
      return { snapshots };
    },
    onError: (err: Error, _vars, ctx) => {
      // Roll back the optimistic update.
      for (const [key, data] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, data);
      }
      push({ title: 'Kunne ikke toaste', description: err.message, tone: 'danger' });
    },
    onSettled: () => {
      // Reconcile against the authoritative count from the server.
      void qc.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  return (
    <button
      type="button"
      onClick={() => !isOwn && mutate.mutate()}
      disabled={isOwn || mutate.isPending}
      aria-pressed={toasted}
      aria-label={toasted ? 'Fjern toast' : 'Toast'}
      title={isOwn ? 'Du kan ikke toaste din egen smaking' : toasted ? 'Fjern toast' : 'Toast denne'}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs transition-colors ${
        isOwn
          ? 'cursor-not-allowed text-mist/60'
          : toasted
            ? 'bg-ruby/15 text-ruby hover:bg-ruby/25'
            : 'text-mist hover:bg-overlay-soft hover:text-ruby'
      }`}
    >
      <Icon name="heart" size={14} className={toasted ? 'fill-current' : undefined} />
      <span className="font-medium">{checkIn.toastCount}</span>
    </button>
  );
}

function StarRating({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const half = value >= star - 0.5 && value < star;
        const full = value >= star;
        return (
          <span key={star} className="relative inline-block h-4 w-4">
            <Icon
              name="star"
              size={16}
              className={full ? 'text-candle' : 'text-mist/30'}
            />
            {half && (
              <span aria-hidden className="absolute inset-0 overflow-hidden" style={{ clipPath: 'inset(0 50% 0 0)' }}>
                <Icon name="star" size={16} className="text-candle" />
              </span>
            )}
          </span>
        );
      })}
      <span className="ml-1 text-xs text-mist">{value.toFixed(1)}</span>
    </div>
  );
}

function servingLabel(style: string): string {
  const map: Record<string, string> = {
    bottle: 'hel flaske',
    glass: 'glass',
    'half-bottle': 'halvflaske',
    magnum: 'magnum',
    decanter: 'karaffel',
    tap: 'tappet',
    can: 'boks',
  };
  return map[style] ?? style;
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'nå';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} t siden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} d siden`;
  return new Date(iso).toLocaleDateString('nb-NO');
}
